import { useLingui } from "@lingui/react/macro";
import { useMemo } from "react";

import { assetName, useAssets } from "@/features/matches";

export type DrillAsset = {
  // id предмета — по нажатию на иконку его описание; у героев и способностей `null`.
  itemId: number | null;
  name: string;
  iconUrl: string | null;
  // Ветка магазина и тир — у предметов; у героев и способностей `null`.
  slot: "weapon" | "vitality" | "spirit" | null;
  tier: number | null;
};

export type DrillAssets = {
  item: (id: number) => DrillAsset;
  hero: (id: number) => DrillAsset;
  ability: (id: number) => DrillAsset;
};

// Названия и иконки для id в задачах: предметы, герои и способности из справочника
// `GET /api/matches/assets`. Нет в справочнике — «Предмет #id» без иконки.
export const useDrillAssets = (): DrillAssets => {
  const { t } = useLingui();
  const { data } = useAssets();
  return useMemo(() => {
    const items = new Map((data?.items ?? []).map((item) => [item.id, item]));
    const heroes = new Map((data?.heroes ?? []).map((hero) => [hero.id, hero]));
    const abilities = new Map((data?.abilities ?? []).map((ability) => [ability.id, ability]));
    return {
      item: (id) => {
        const item = items.get(id);
        return item === undefined
          ? { itemId: null, name: t`Item #${id}`, iconUrl: null, slot: null, tier: null }
          : {
              itemId: id,
              name: assetName(item.name),
              iconUrl: item.icon_url,
              slot: item.slot,
              tier: item.tier,
            };
      },
      hero: (id) => {
        const hero = heroes.get(id);
        return {
          itemId: null,
          name: hero === undefined ? t`Hero #${id}` : assetName(hero.name),
          iconUrl: hero?.icon_url ?? null,
          slot: null,
          tier: null,
        };
      },
      ability: (id) => {
        const ability = abilities.get(id);
        return {
          itemId: null,
          name: ability === undefined ? t`Ability #${id}` : assetName(ability.name),
          iconUrl: ability?.icon_url ?? null,
          slot: null,
          tier: null,
        };
      },
    };
  }, [data, t]);
};
